import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Module-level token so refreshing the page still uses it.
const TOKEN_KEY = "therapynote_token";

export function getToken() {
  return typeof localStorage !== "undefined"
    ? localStorage.getItem(TOKEN_KEY)
    : null;
}

export function setToken(t) {
  if (typeof localStorage === "undefined") return;
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

const client = axios.create({ baseURL: API, withCredentials: true, timeout: 45000 });

client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function isRetriable(err) {
  if (!err?.response) return true;
  const s = err.response.status;
  return s === 404 || (s >= 500 && s < 600);
}

async function withRetry(fn, onRetry) {
  const backoffs = [1500, 3000];
  let lastErr;
  for (let i = 0; i < 3; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (i < 2 && isRetriable(e)) {
        if (onRetry) onRetry(i + 1, 3);
        await sleep(backoffs[i]);
        continue;
      }
      break;
    }
  }
  const detail =
    lastErr?.response?.data?.detail || lastErr?.message || "Request failed.";
  const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  err.status = lastErr?.response?.status;
  err.cause = lastErr;
  throw err;
}

// ----- Auth -----
export async function signup({ email, password, name, clinic_name }) {
  const { data } = await client.post("/auth/signup", {
    email,
    password,
    name,
    clinic_name,
  });
  setToken(data.token);
  return data.user;
}

export async function login({ email, password }) {
  const { data } = await client.post("/auth/login", { email, password });
  setToken(data.token);
  return data.user;
}

export async function googleCallback(session_id) {
  const { data } = await client.post("/auth/google/callback", { session_id });
  return data.user;
}

export async function me() {
  const { data } = await client.get("/auth/me");
  return data.user;
}

export async function logout() {
  try {
    await client.post("/auth/logout");
  } catch (e) {
    // ignore — still clear local token
  }
  setToken(null);
}

// ----- Patients -----
export async function listPatients() {
  const { data } = await client.get("/patients");
  return data;
}

export async function createPatient({ patient_id, initials }) {
  const { data } = await client.post("/patients", { patient_id, initials });
  return data;
}

export async function listPlansForPatient(patient_id) {
  const { data } = await client.get(
    `/patients/${encodeURIComponent(patient_id)}/plans`
  );
  return data;
}

// ----- Plans -----
export async function createPlan({ patient_id, initials, type = "evaluation" }) {
  const { data } = await client.post("/plans", { patient_id, initials, type });
  return data;
}

export async function updatePlan(plan_id, patch) {
  const { data } = await client.put(`/plans/${plan_id}`, patch);
  return data;
}

export async function listAllPlans() {
  const { data } = await client.get("/plans");
  return data;
}

// ----- Audio -----
export async function uploadAudio({ plan_id, tab, blob, filename }) {
  const form = new FormData();
  form.append("file", blob, filename || `audio-${Date.now()}.webm`);
  form.append("plan_id", plan_id);
  form.append("tab", tab);
  const { data } = await client.post("/audio/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { audio_path, url }
}

export function audioStreamUrl(path) {
  if (!path) return null;
  // Public path served with auth header through fetch-as-blob pattern.
  return `${API}/audio/${path}`;
}

export async function fetchAudioBlobUrl(path) {
  if (!path) return null;
  const res = await client.get(`/audio/${path}`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

// ----- AI Extraction -----
export async function extractClinicalNotes(transcript, tabType, onRetry, therapyType) {
  return withRetry(async () => {
    const { data } = await client.post("/ai/extract", {
      transcript,
      tab_type: tabType,
      therapy_type: therapyType || "physiotherapy",
    });
    return data;
  }, onRetry);
}

// ----- Profile -----
export async function updateProfile(patch) {
  const { data } = await client.put("/auth/me", patch);
  return data.user;
}

// ----- Plan submit -----
export async function submitPlan(plan_id) {
  const { data } = await client.post(`/plans/${plan_id}/submit`);
  return data;
}

// ----- Billing -----
export async function createCheckout(origin_url, package_id = "solo_monthly") {
  const { data } = await client.post("/billing/checkout", {
    package_id,
    origin_url,
  });
  return data; // { url, session_id }
}

export async function getBillingStatus(session_id) {
  const { data } = await client.get(`/billing/status/${session_id}`);
  return data;
}

export async function confirmBilling(session_id) {
  const { data } = await client.post(`/billing/confirm/${session_id}`);
  return data;
}

// ----- Password reset -----
export async function requestPasswordReset(email, origin_url) {
  const { data } = await client.post("/auth/password-reset/request", {
    email,
    origin_url,
  });
  return data;
}

export async function confirmPasswordReset(token, new_password) {
  const { data } = await client.post("/auth/password-reset/confirm", {
    token,
    new_password,
  });
  return data;
}

// ----- Whisper transcription -----
export async function getTranscribeStatus() {
  const { data } = await client.get("/transcribe/status");
  return data;
}

export async function transcribeAudio(blob, { plan_id, tab, demo } = {}) {
  const form = new FormData();
  form.append("file", blob, "audio.webm");
  if (plan_id) form.append("plan_id", plan_id);
  if (tab) form.append("tab", tab);
  const path = demo ? "/transcribe/demo" : "/transcribe";
  const { data } = await client.post(path, form);
  return data; // { text }
}
